import React, { useState } from "react";
import { useAnketaI18n } from "src/internationalization";
import { OpsanioIcon } from "src/assets/images/opsanio/opsanio_icon";
import { HomepageLangSwitch } from "./lang-switch";

/*
 * Homepage footer - rebuilt for a clean, aligned grid (the old EuiFlexGroup
 * space-between layout wrapped unevenly and split columns into ragged
 * sub-columns). UI/UX laws applied:
 *  - Proximity / alignment: one even CSS grid, one link per row per column.
 *  - Hick's / DRY: no duplicated copyright or Imprint/Contact; legal lives in
 *    the bottom bar only.
 *  - Jakob's Law: conventional brand + nav-columns + legal bottom bar.
 *  - No layout shift: links hover by colour only (no inserted bullet).
 *  - Product-first: opsanio mark leads; the company sits quietly underneath.
 * Links target sections that actually exist on the page.
 */

const C = {
  accent: "var(--ak-accent)",
  bg: "var(--ak-navy)",
  border: "var(--ak-on-navy-border)",
  textPrimary: "var(--ak-on-navy)",
  textSecondary: "var(--ak-on-navy-secondary)",
  textMuted: "var(--ak-on-navy-muted)",
  sans: "system-ui,-apple-system,sans-serif",
  mono: '"JetBrains Mono","Fira Code",monospace',
};

interface Link {
  href: string;
  label: string;
}

const FooterLink: React.FC<{ link: Link }> = ({ link }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={link.href}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        fontSize: "0.86rem",
        lineHeight: 1.4,
        color: hover ? C.accent : C.textSecondary,
        textDecoration: "none",
        transition: "color 0.18s",
        fontFamily: C.sans,
      }}
    >
      {link.label}
    </a>
  );
};

const FooterColumn: React.FC<{ title: string; links: Link[] }> = ({ title, links }) => (
  <nav style={{ display: "flex", flexDirection: "column", gap: 12 }} aria-label={title}>
    <div
      style={{
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: C.textMuted,
        marginBottom: 4,
      }}
    >
      {title}
    </div>
    {links.map((l) => (
      <FooterLink key={l.href + l.label} link={l} />
    ))}
  </nav>
);

export const AutonubilHomepageFooter = (): JSX.Element => {
  const { translate, currentLang } = useAnketaI18n();
  const sp = "anketa.autonubil.home.services";
  const fp = "anketa.autonubil.home.footer";
  const t = (k: string, d: string) => translate(k, { defaultMessage: d } as any);

  const productLinks: Link[] = [
    { href: "#product", label: t(`${fp}.link.platform`, "Platform") },
    { href: "#operating-models", label: t("anketa.autonubil.home.models.eyebrow", "Operating models") },
    { href: "#opsanio-ar", label: t(`${fp}.link.ar`, "Detect & investigate") },
    { href: "#integrations", label: t(`${fp}.link.integrations`, "Integrations") },
  ];

  const serviceLinks: Link[] = [
    { href: "#service-opsanio", label: translate(`${sp}.opsanio.title`) },
    { href: "#service-kubernetes", label: translate(`${sp}.kubernetes.title`) },
    { href: "#service-docker", label: translate(`${sp}.docker.title`) },
    { href: "#service-devops", label: translate(`${sp}.devops.title`) },
    { href: "#service-automation", label: translate(`${sp}.automation.title`) },
  ];

  const companyLinks: Link[] = [
    { href: "#about", label: translate("anketa.autonubil.about") },
    { href: "/home/contact", label: translate("anketa.autonubil.contact") },
    { href: "#partners", label: t(`${fp}.link.partners`, "Partners") },
  ];

  return (
    <footer
      style={{
        background: C.bg,
        borderTop: `1px solid ${C.border}`,
        fontFamily: C.sans,
        color: C.textSecondary,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "64px clamp(24px, 5vw, 48px) 40px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "40px clamp(24px, 4vw, 56px)",
          alignItems: "start",
        }}
      >
        {/* Brand - product first, company underneath */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 280 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 11 }}>
            <span
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "var(--ak-on-navy-surface-2)",
                border: `1px solid ${C.border}`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <OpsanioIcon width={18} height={21} aria-hidden="true" />
            </span>
            <span style={{ fontSize: "1.1rem", fontWeight: 800, letterSpacing: "-0.02em", color: C.textPrimary }}>
              opsanio
            </span>
          </div>
          <p style={{ margin: 0, fontSize: "0.85rem", lineHeight: 1.6, color: C.textSecondary, maxWidth: 260 }}>
            {t(
              `${fp}.tagline`,
              "Sovereign IT visibility, SIEM and compliance - one appliance, operated by autonubil."
            )}
          </p>
          <div
            style={{
              width: 40,
              height: 2,
              borderRadius: 1,
              background: `linear-gradient(90deg, ${C.accent} 0%, var(--ak-accent-2) 100%)`,
            }}
          />
          <p style={{ margin: 0, fontSize: "0.78rem", color: C.textMuted }}>
            {t(`${fp}.operatedBy`, "Operated by autonubil GmbH")}
          </p>
        </div>

        <FooterColumn title={translate("anketa.opsanio")} links={productLinks} />
        <FooterColumn title={translate("anketa.autonubil.services")} links={serviceLinks} />
        <FooterColumn title={translate("anketa.user.company")} links={companyLinks} />
      </div>

      {/* Legal bottom bar - copyright + imprint + language (single source) */}
      <div style={{ borderTop: `1px solid ${C.border}` }}>
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "18px clamp(24px, 5vw, 48px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <span style={{ fontSize: "0.75rem", color: C.textMuted, fontFamily: C.mono }}>
            © 2023-2026 autonubil GmbH
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            <FooterLink link={{ href: "/home/imprint", label: translate("anketa.autonubil.imprint.title") }} />
            {/* Server-rendered, outside the SPA (routes/security.ts) - a plain
                href so the browser leaves react-router rather than 404ing.
                German is the default; the English page has its own path. */}
            <FooterLink
              link={{
                href: currentLang === "en" ? "/security/en" : "/security",
                label: translate("anketa.autonubil.security.title"),
              }}
            />
            <HomepageLangSwitch />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AutonubilHomepageFooter;
